import { api } from "./client";

// Обедни менюта от „Маркиране" (BE `app/external/lunch_menus`). Живеят в org
// базата като менюта с kind="lunch", закачени за ресторанта от сесията.
// Дни от седмицата: 0 = неделя, 1 = понеделник … 6 = събота.

export type LunchMenuStatus = "live" | "upcoming" | "past";

export interface LunchMenuItem {
  itemId: number;
  // Decimal → JSON низ
  price: string;
  // {"bg": "300 г"}; липсващ език → размерът на продукта
  unitAndSizeLangs: Record<string, string> | null;
  // Порции на ден; 0 = без ограничение
  dailyLimit: number;
}

export interface LunchMenuSummary {
  id: number;
  name: string;
  dateFrom: string | null;
  dateTo: string | null;
  weekdays: number[] | null;
  timeFrom: string | null;
  timeTo: string | null;
  status: LunchMenuStatus;
  // Следващият (или днешният) активен ден; null за минали
  nextDate: string | null;
  itemCount: number;
  createdAt: string;
  createdByName: string | null;
}

export interface LunchMenu extends LunchMenuSummary {
  items: LunchMenuItem[];
}

export type LunchConflictResolution = "finish" | "delete";

export interface LunchMenuIn {
  name: string;
  dateFrom: string;
  dateTo: string | null;
  weekdays: number[] | null;
  timeFrom: string;
  timeTo: string;
  items: {
    itemId: number;
    price: string;
    unitAndSizeLangs: Record<string, string> | null;
    dailyLimit: number;
  }[];
  resolveConflicts?: LunchConflictResolution;
}

// 409 при застъпване: detail = {code, conflicts}
export const LUNCH_OVERLAP_CODE = "lunch_menu_overlap";

export interface LunchMenuConflict extends Omit<LunchMenuSummary, "itemCount"> {
  // Какво прави „Приключи старото": започнало в по-ранен ден → спира ден
  // преди новото; същия ден или по-късно → спира веднага („Минали")
  finishMode: "before_new" | "now";
}

/** Конфликтите от 409 `lunch_menu_overlap`, иначе null. */
export const getLunchOverlapConflicts = (
  error: unknown,
): LunchMenuConflict[] | null => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const detail = response?.data?.detail as
    | { code?: string; conflicts?: LunchMenuConflict[] }
    | undefined;
  if (response?.status !== 409 || detail?.code !== LUNCH_OVERLAP_CODE) return null;
  return detail.conflicts ?? [];
};

// 409 при поръчка от обедно меню, което вече не е активно (BE
// `bills/service_lunch.LUNCH_ENDED_CODE`) — часът е минал, приключено или изтрито
export const LUNCH_ENDED_CODE = "lunch_menu_ended";

// 409 при изчерпани порции от обедно меню (BE
// `bills/service_lunch_limits.LUNCH_LIMIT_CODE`, касата — edge/lunch.py)
export const LUNCH_LIMIT_CODE = "lunch_limit_reached";

export interface LunchLimitExceeded {
  lunchMenuId: number;
  itemId: number;
  // Колко порции остават (0 = изчерпано)
  remaining: number;
}

/** Продуктите от 409 `lunch_limit_reached`, иначе null. */
export const getExceededLunchLimits = (error: unknown): LunchLimitExceeded[] | null => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const detail = response?.data?.detail as
    | { code?: string; items?: LunchLimitExceeded[] }
    | undefined;
  if (response?.status !== 409 || detail?.code !== LUNCH_LIMIT_CODE) return null;
  return detail.items ?? [];
};

/** Обедните менюта от 409 `lunch_menu_ended`, иначе null. */
export const getEndedLunchMenuIds = (error: unknown): number[] | null => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const detail = response?.data?.detail as { code?: string; lunchMenuIds?: number[] } | undefined;
  if (response?.status !== 409 || detail?.code !== LUNCH_ENDED_CODE) return null;
  return detail.lunchMenuIds ?? [];
};

export const getLunchMenus = async (): Promise<LunchMenuSummary[]> =>
  (await api.get<LunchMenuSummary[]>("/lunch-menus")).data;

export const getLunchMenu = async (menuId: number): Promise<LunchMenu> =>
  (await api.get<LunchMenu>(`/lunch-menus/${menuId}`)).data;

export const createLunchMenu = async (data: LunchMenuIn): Promise<LunchMenu> =>
  (await api.post<LunchMenu>("/lunch-menus", data)).data;

export const updateLunchMenu = async (
  menuId: number,
  data: LunchMenuIn,
): Promise<LunchMenu> =>
  (await api.put<LunchMenu>(`/lunch-menus/${menuId}`, data)).data;

export const finishLunchMenu = async (menuId: number): Promise<LunchMenu> =>
  (await api.post<LunchMenu>(`/lunch-menus/${menuId}/finish`)).data;

export const deleteLunchMenu = async (menuId: number): Promise<void> => {
  await api.delete(`/lunch-menus/${menuId}`);
};
